import { Monitor, Moon, Sun } from 'lucide-react';
import { ThemePref, setThemePref, useTheme } from '../lib/theme';

const OPTIONS: { value: ThemePref; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/** Sidebar theme switch: a 3-way segmented control, or one cycling button when collapsed. */
export default function ThemeToggle({ collapsed = false }: { collapsed?: boolean }) {
  const [pref] = useTheme();

  if (collapsed) {
    const idx = OPTIONS.findIndex((o) => o.value === pref);
    const cur = OPTIONS[idx];
    const next = OPTIONS[(idx + 1) % OPTIONS.length];
    return (
      <button
        onClick={() => setThemePref(next.value)}
        title={`Theme: ${cur.label} (click for ${next.label})`}
        className="flex w-full justify-center rounded-lg py-2 text-ink-500 hover:bg-ink-800 hover:text-ink-300 transition-colors"
      >
        <cur.icon className="h-[18px] w-[18px]" />
      </button>
    );
  }

  return (
    <div role="radiogroup" aria-label="Theme" className="mx-1 flex rounded-lg bg-ink-950/60 p-0.5">
      {OPTIONS.map((o) => {
        const on = pref === o.value;
        return (
          <button
            key={o.value}
            role="radio"
            aria-checked={on}
            onClick={() => setThemePref(o.value)}
            title={`${o.label} theme`}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-1 text-xs font-medium transition-colors ${
              on ? 'bg-ink-700 text-white shadow-sm' : 'text-ink-500 hover:text-ink-300'
            }`}
          >
            <o.icon className="h-3.5 w-3.5" />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
